import { useState } from 'react'

import { useAuth } from '@/hooks/use-auth'
import {
    useEInvoiceCredential,
    useRemoveEInvoiceCredential,
    useVerifyEInvoiceCredential,
} from '@/hooks/use-einvoice-credential'
import { ApiError } from '@/lib/api'
import { m } from '@/lib/paraglide/messages'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

export function MisaAccountCard(
    props: {
        canExport?: boolean
        onConnected?: () => void
        compact?: boolean
    } = {},
) {
    const { user, impersonating } = useAuth()
    return (
        <MisaAccountForm
            key={`${impersonating?.actor.id ?? user?.id}:${user?.id}`}
            {...props}
        />
    )
}

function MisaAccountForm({
    canExport,
    onConnected,
    compact,
}: {
    canExport?: boolean
    onConnected?: () => void
    compact?: boolean
}) {
    const { hasPermission, isImpersonating } = useAuth()
    const allowed = canExport ?? hasPermission('invoices.export')
    const {
        data: credential,
        isLoading,
        isError,
        refetch,
    } = useEInvoiceCredential()
    const verify = useVerifyEInvoiceCredential()
    const remove = useRemoveEInvoiceCredential()
    const [username, setUsername] = useState<string | null>(null)
    const [password, setPassword] = useState('')
    const [error, setError] = useState('')
    const [successUser, setSuccessUser] = useState('')
    if (!allowed) return null
    if (isLoading) {
        return (
            <Card>
                <CardContent className="pt-6" role="status">
                    {m.einvoice_credential_loading()}
                </CardContent>
            </Card>
        )
    }
    if (isError) {
        return (
            <Card>
                <CardContent className="space-y-3 pt-6">
                    <p className="text-sm text-destructive">
                        {m.einvoice_credential_load_error()}
                    </p>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => void refetch()}
                    >
                        {m.einvoice_credential_retry()}
                    </Button>
                </CardContent>
            </Card>
        )
    }

    const configured = Boolean(credential?.configured)
    const storedUsername = credential?.misa_username ?? ''
    const currentUsername = (username ?? storedUsername).trim()
    const handleVerify = async () => {
        setError('')
        setSuccessUser('')
        if (
            !currentUsername ||
            (!password && (!configured || currentUsername !== storedUsername))
        ) {
            setError(m.einvoice_credential_required_field())
            return
        }
        try {
            const response = await verify.mutateAsync({
                misa_username: currentUsername,
                ...(password ? { password } : {}),
            })
            setSuccessUser(response?.misa_username ?? currentUsername)
            setUsername(null)
            setPassword('')
            onConnected?.()
        } catch (cause) {
            setError(
                cause instanceof ApiError
                    ? cause.message
                    : cause instanceof Error
                      ? cause.message
                      : String(cause),
            )
        }
    }

    return (
        <Card>
            {!compact ? (
                <CardHeader>
                    <CardTitle>{m.einvoice_credential_title()}</CardTitle>
                    <div className="space-y-1 text-sm text-muted-foreground">
                        <p>{m.einvoice_credential_howto_1()}</p>
                        <p>{m.einvoice_credential_howto_2()}</p>
                        <p>{m.einvoice_credential_howto_3()}</p>
                    </div>
                </CardHeader>
            ) : null}
            <CardContent className={compact ? 'space-y-4 pt-0' : 'space-y-4'}>
                {credential?.invalid_since ? (
                    <div className="break-words rounded-md border border-destructive/50 bg-destructive/5 p-3 text-sm text-destructive">
                        <p>{m.einvoice_credential_invalid_desc()}</p>
                        {credential.invalid_reason ? (
                            <p className="mt-1 break-words">
                                {credential.invalid_reason}
                            </p>
                        ) : null}
                    </div>
                ) : null}
                {successUser ? (
                    <p className="text-sm text-green-700">
                        {m.einvoice_credential_connected({
                            userName: successUser,
                        })}
                    </p>
                ) : null}
                <div className="space-y-1.5">
                    <Label htmlFor="misa-username">
                        {m.einvoice_credential_username()}
                    </Label>
                    <Input
                        id="misa-username"
                        value={username ?? storedUsername}
                        onChange={(event) => setUsername(event.target.value)}
                        placeholder={m.einvoice_credential_username_placeholder()}
                        autoComplete="username"
                        disabled={
                            isImpersonating ||
                            verify.isPending ||
                            remove.isPending
                        }
                    />
                </div>
                <div className="space-y-1.5">
                    <Label htmlFor="misa-password">
                        {m.einvoice_credential_password()}
                    </Label>
                    <Input
                        id="misa-password"
                        type="password"
                        value={password}
                        onChange={(event) => setPassword(event.target.value)}
                        placeholder={m.einvoice_credential_password_placeholder()}
                        autoComplete="new-password"
                        disabled={
                            isImpersonating ||
                            verify.isPending ||
                            remove.isPending
                        }
                    />
                    {configured ? (
                        <p className="text-xs text-muted-foreground">
                            {m.einvoice_credential_password_keep_hint()}
                        </p>
                    ) : null}
                </div>
                {error ? (
                    <p className="text-sm text-destructive">{error}</p>
                ) : null}
                {credential?.verified_at && !credential.invalid_since ? (
                    <p className="text-xs text-muted-foreground">
                        {m.einvoice_credential_verified_at({
                            time: new Intl.DateTimeFormat('vi-VN', {
                                dateStyle: 'short',
                                timeStyle: 'short',
                                timeZone: 'Asia/Ho_Chi_Minh',
                            }).format(new Date(credential.verified_at)),
                        })}
                    </p>
                ) : null}
                <div className="flex flex-wrap justify-end gap-2">
                    {configured ? (
                        <Button
                            type="button"
                            variant="outline"
                            disabled={
                                isImpersonating ||
                                remove.isPending ||
                                verify.isPending
                            }
                            onClick={() => {
                                setError('')
                                void remove
                                    .mutateAsync()
                                    .then(() => {
                                        setUsername(null)
                                        setPassword('')
                                        setSuccessUser('')
                                    })
                                    .catch((cause) =>
                                        setError(
                                            cause instanceof Error
                                                ? cause.message
                                                : String(cause),
                                        ),
                                    )
                            }}
                        >
                            {m.einvoice_credential_remove()}
                        </Button>
                    ) : null}
                    <Button
                        type="button"
                        disabled={
                            isImpersonating ||
                            verify.isPending ||
                            remove.isPending
                        }
                        onClick={() => void handleVerify()}
                    >
                        {verify.isPending
                            ? m.einvoice_credential_testing()
                            : m.einvoice_credential_test()}
                    </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                    {m.einvoice_credential_privacy()}
                </p>
            </CardContent>
        </Card>
    )
}
